// API Key Manager Page (OF-167)
//
// Talks to backend/src/routes/apikey.routes.ts, whose contract is:
//   GET    /api-keys → rows { id, key_prefix, name, scope, rate_limit, last_used_at,
//                             expires_at, revoked, organization_id, license_id, created_at }
//   POST   /api-keys ← { name, scope, expiresAt?, licenseId? }   ONE scope per key (D-50)
//          → 201 FLAT { ...row, key, message }  (the plaintext `key` is shown once)
//   DELETE /api-keys/:id → 204, empty body
// This page used to post `scopes: [...]` + `expires_at` and read a `{ key, api_key }`
// envelope, none of which the server has ever had — every mint 400'd.
//
// The `validate` key's licence picker asks the SERVER for what it needs:
//   GET /licenses?class=install&limit=100   and   GET /licenses?class=platform&limit=100
// (+ `&search=` once the operator types). GET /licenses reads `limit` (default 20,
// capped at 100) and orders newest-first; it has never read `pageSize`. This page
// used to send `?pageSize=200` and filter saas rows out client-side - so the picker
// saw only the 20 NEWEST licences, a window every self-serve signup (a saas licence)
// refills, and an operator could not mint or rotate a validate key for any older
// install. When a class has more than one page the picker says so and offers search.
//
// Two columns render fields the Keyed API (19A D-47/D-50) will add and the server
// does NOT emit yet: `max_calls_per_day` (quota) and `push_status` (the licence →
// data-plane key push). They show when the server sends them and say nothing —
// rather than invent a value — when it does not.
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { apiRequest } from '@/lib/supabase';
import { formatRelativeTime } from '@/lib/utils';
import { ConfirmDialog } from '@/components/shared/ConfirmDialog';
import { useToast } from '@/components/shared/ToastProvider';
import { API_KEY_SCOPES, scopeInfo } from '@/lib/apiKeyScopes';
import { Plus, Copy, Trash2, Eye, EyeOff, Key } from 'lucide-react';

type PushStatus = 'pushed' | 'pending' | 'drift';
const PUSH_STATUSES: readonly PushStatus[] = ['pushed', 'pending', 'drift'];

interface ApiKey {
  id: string;
  name: string;
  key_prefix: string;
  scope: string;
  created_at: string | null;
  last_used_at: string | null;
  expires_at: string | null;
  revoked: boolean;
  organization_id: string | null;
  license_id: string | null;
  // undefined = the server did not say; null = the server says "no cap".
  max_calls_per_day: number | null | undefined;
  push_status: PushStatus | undefined;
  // undefined = the server did not count; never rendered as 0.
  call_count: number | undefined;
}

// Tolerate either a bare array or a wrapped { data: [] } shape, and sanitize each
// record so per-field renders never throw when optional fields are absent.
function normalizeKeys(raw: unknown): ApiKey[] {
  let list: unknown = raw;
  if (raw && typeof raw === 'object' && !Array.isArray(raw)) {
    const obj = raw as Record<string, unknown>;
    list = obj.data ?? obj.api_keys ?? obj.keys ?? [];
  }
  if (!Array.isArray(list)) return [];
  return list.map((item): ApiKey => {
    const k = (item ?? {}) as Record<string, unknown>;
    const push = k.push_status;
    return {
      id: String(k.id ?? `k${Math.random().toString(36).slice(2)}`),
      name: typeof k.name === 'string' ? k.name : 'Unnamed key',
      key_prefix: typeof k.key_prefix === 'string' ? k.key_prefix : '',
      scope: typeof k.scope === 'string' ? k.scope : '',
      created_at: typeof k.created_at === 'string' ? k.created_at : null,
      last_used_at: typeof k.last_used_at === 'string' ? k.last_used_at : null,
      expires_at: typeof k.expires_at === 'string' ? k.expires_at : null,
      revoked: k.revoked === true,
      organization_id: typeof k.organization_id === 'string' ? k.organization_id : null,
      license_id: typeof k.license_id === 'string' ? k.license_id : null,
      max_calls_per_day: 'max_calls_per_day' in k
        ? (typeof k.max_calls_per_day === 'number' ? k.max_calls_per_day : null)
        : undefined,
      push_status: PUSH_STATUSES.includes(push as PushStatus) ? (push as PushStatus) : undefined,
      call_count: typeof k.call_count === 'number' ? k.call_count : undefined,
    };
  });
}

interface LicenseOption {
  id: string;
  label: string;
}

interface LicenseResult {
  options: LicenseOption[];
  /** Rows the server returned across the classes asked for. */
  loaded: number;
  /** Rows that match across those classes (the server's pagination.total). */
  total: number;
}

// The two classes a `validate` key can authenticate for: an install phones home,
// a platform licence is the hosted/multi-org contract. A saas-class record has no
// JWT and no /validate involvement, ever - so it is never asked for.
const PICKABLE_CLASSES = ['install', 'platform'] as const;
// GET /licenses caps `limit` at 100 (license.routes.ts); asking for more is silently
// the same 100.
const LICENSE_PAGE_SIZE = 100;

// GET /licenses -> { data: [...], pagination: { total, page, limit, totalPages } }
function normalizeLicenses(raw: unknown): LicenseResult {
  const obj = (raw ?? {}) as Record<string, unknown>;
  const list = Array.isArray(raw) ? raw : Array.isArray(obj.data) ? obj.data : [];
  const options: LicenseOption[] = [];
  for (const item of list) {
    const l = (item ?? {}) as Record<string, any>;
    // Belt and braces: the query already asks for install/platform only.
    if (typeof l.id !== 'string' || l.license_class === 'saas') continue;
    const org = l.organization?.name ?? l.organization_name;
    const display = l.license_id ?? l.license_key ?? l.id;
    options.push({ id: l.id, label: `${display} · ${org ?? l.tier ?? 'license'}` });
  }
  const pagination = (obj.pagination ?? {}) as Record<string, unknown>;
  // No pagination block = the server did not say there is more; never claim it.
  const total = typeof pagination.total === 'number' ? pagination.total : list.length;
  return { options, loaded: list.length, total };
}

async function fetchLicenseOptions(search: string): Promise<LicenseResult> {
  const pages = await Promise.all(
    PICKABLE_CLASSES.map((cls) => {
      const q = new URLSearchParams({ class: cls, limit: String(LICENSE_PAGE_SIZE) });
      if (search) q.set('search', search);
      return apiRequest<unknown>(`/licenses?${q.toString()}`).then(normalizeLicenses);
    }),
  );
  return {
    options: pages.flatMap((p) => p.options),
    loaded: pages.reduce((n, p) => n + p.loaded, 0),
    total: pages.reduce((n, p) => n + p.total, 0),
  };
}

// A date-input day → the END of that UTC day, as the ISO instant the server's
// z.string().datetime() requires ('2027-03-01' alone is rejected).
function expiryToIso(day: string): string {
  return new Date(`${day}T23:59:59.999Z`).toISOString();
}

interface CreateKeyForm {
  name: string;
  scope: string; // '' = none chosen yet — least privilege, never default to admin
  licenseId: string;
  expires: string; // yyyy-mm-dd or ''
}
const EMPTY_FORM: CreateKeyForm = { name: '', scope: '', licenseId: '', expires: '' };

type LicensesStatus = 'idle' | 'loading' | 'ready' | 'error';

const visuallyHidden: React.CSSProperties = {
  position: 'absolute', width: 1, height: 1, overflow: 'hidden', clip: 'rect(0 0 0 0)', whiteSpace: 'nowrap',
};
const fieldLabel: React.CSSProperties = { display: 'block', fontSize: '12px', fontWeight: 500, color: 'var(--text-secondary)', marginBottom: '6px' };
const fieldInput: React.CSSProperties = { width: '100%', padding: '8px 10px', borderRadius: '6px', border: '1px solid var(--border)', fontSize: '13px', outline: 'none', boxSizing: 'border-box' };
const th: React.CSSProperties = { padding: '8px 16px', textAlign: 'left', fontSize: '11px', fontWeight: 600, textTransform: 'uppercase', color: 'var(--text-muted)' };
const td: React.CSSProperties = { padding: '14px 16px', verticalAlign: 'top' };

const PUSH_STYLE: Record<PushStatus, { bg: string; fg: string }> = {
  pushed: { bg: '#DCFCE7', fg: '#166534' },
  pending: { bg: '#FEF3C7', fg: '#92400E' },
  drift: { bg: '#FEE2E2', fg: '#991B1B' },
};

function quotaText(q: ApiKey['max_calls_per_day']): string {
  if (q === undefined) return '—';
  if (q === null) return 'Unlimited';
  return `${q.toLocaleString()}/day`;
}

export function ApiKeyManagerPage() {
  const [keys, setKeys] = useState<ApiKey[]>([]);
  const [loading, setLoading] = useState(true);
  // A failed list fetch is NOT an empty key set: "No API keys yet" would tell an
  // operator who cannot reach the server that no keys exist.
  const [keysError, setKeysError] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [newKey, setNewKey] = useState<string | null>(null);
  const [showNewKey, setShowNewKey] = useState(true);
  const [form, setForm] = useState<CreateKeyForm>(EMPTY_FORM);
  const [formError, setFormError] = useState<string | null>(null);
  const [licenses, setLicenses] = useState<LicenseOption[]>([]);
  const [licensesStatus, setLicensesStatus] = useState<LicensesStatus>('idle');
  const [licenseSearch, setLicenseSearch] = useState('');
  const [licenseRetry, setLicenseRetry] = useState(0);
  // loaded < total: the picker is showing a page, not the fleet.
  const [licenseCounts, setLicenseCounts] = useState<{ loaded: number; total: number } | null>(null);
  // Sticky: once the UNFILTERED list proved bigger than a page, the search box stays
  // (otherwise it would vanish the moment a search narrowed the result to one row).
  const [licensesSearchable, setLicensesSearchable] = useState(false);
  // The operator's pick survives a re-search that no longer lists it.
  const [pickedLicense, setPickedLicense] = useState<LicenseOption | null>(null);
  const licenseReq = useRef(0);
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const [revokeTarget, setRevokeTarget] = useState<{ id: string; name: string } | null>(null);
  const { toast } = useToast();

  const selected = scopeInfo(form.scope);
  const needsLicense = selected?.needsLicense === true;

  const loadKeys = useCallback(() => {
    setLoading(true);
    setKeysError(null);
    apiRequest<unknown>('/api-keys')
      .then(data => setKeys(normalizeKeys(data)))
      .catch(err => {
        setKeys([]);
        setKeysError(err instanceof Error && err.message ? err.message : 'Request failed');
      })
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { loadKeys(); }, [loadKeys]);

  // The licence picker loads on demand - only a `validate` key needs one - and asks
  // the server for exactly what it needs (see the header). Typing re-queries
  // (debounced); a stale response never overwrites a newer one.
  useEffect(() => {
    if (!needsLicense) return;
    const term = licenseSearch.trim();
    const handle = setTimeout(() => {
      const req = ++licenseReq.current;
      setLicensesStatus('loading');
      fetchLicenseOptions(term)
        .then(result => {
          if (req !== licenseReq.current) return;
          setLicenses(result.options);
          setLicenseCounts({ loaded: result.loaded, total: result.total });
          if (term === '' && result.loaded < result.total) setLicensesSearchable(true);
          setLicensesStatus('ready');
        })
        .catch(() => { if (req === licenseReq.current) setLicensesStatus('error'); });
    }, term ? 300 : 0);
    return () => clearTimeout(handle);
  }, [needsLicense, licenseSearch, licenseRetry]);

  const canSubmit =
    !creating && form.name.trim() !== '' && form.scope !== '' && (!needsLicense || form.licenseId !== '');

  const createKey = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError(null);
    if (!canSubmit) return;

    let expiresAt: string | undefined;
    if (form.expires) {
      expiresAt = expiryToIso(form.expires);
      if (!(new Date(expiresAt).getTime() > Date.now())) {
        setFormError('Expiry must be in the future.');
        return;
      }
    }

    setCreating(true);
    try {
      // The server's body — one scope, camelCase, an ISO instant. Optional fields
      // are OMITTED, never null: z.string().datetime().optional() rejects null.
      const body: Record<string, string> = { name: form.name.trim(), scope: form.scope };
      if (needsLicense) body.licenseId = form.licenseId;
      if (expiresAt) body.expiresAt = expiresAt;

      const result = await apiRequest<Record<string, unknown>>('/api-keys', {
        method: 'POST',
        body: JSON.stringify(body),
      });
      if (typeof result?.key !== 'string') {
        // The row exists server-side but we were not handed the secret. Say so —
        // never show a placeholder that looks like a key.
        toast({ kind: 'error', title: 'API key not returned', body: 'The key was created but the server did not return it. Revoke it and mint a new one.' });
        return;
      }
      setNewKey(result.key);
      setKeys(prev => [...normalizeKeys([result]), ...prev]);
      setForm(EMPTY_FORM);
      setPickedLicense(null);
    } catch (err) {
      // Surface the real failure — never fabricate a key (a fake key would look real
      // but authenticate nothing).
      toast({ kind: 'error', title: 'Failed to create API key', body: err instanceof Error ? err.message : 'Request failed' });
    } finally {
      setCreating(false);
    }
  };

  const revokeKey = async (id: string, name: string) => {
    try {
      await apiRequest(`/api-keys/${id}`, { method: 'DELETE' });
    } catch (err) {
      // A revoke the server refused must not look like one it performed: the
      // operator believing a leaked key is dead is worse than an error dialog.
      toast({ kind: 'error', title: `Failed to revoke API key "${name}"`, body: err instanceof Error ? err.message : 'Request failed' });
      return;
    }
    setKeys(prev => prev.map(k => k.id === id ? { ...k, revoked: true } : k));
    toast({ kind: 'success', title: `API key "${name}" revoked.` });
  };

  const copyKey = (text: string, id: string) => {
    navigator.clipboard.writeText(text);
    setCopiedId(id);
    setTimeout(() => setCopiedId(null), 2000);
  };

  const chooseScope = (value: string) => {
    if (form.scope === value) return;
    setForm(prev => ({ ...prev, scope: value, licenseId: '' }));
    setPickedLicense(null);
  };

  const chooseLicense = (id: string) => {
    setForm(prev => ({ ...prev, licenseId: id }));
    setPickedLicense(licenses.find(l => l.id === id) ?? (pickedLicense?.id === id ? pickedLicense : null));
  };

  // What the <select> lists: this page of results, plus the current pick if a search
  // has since filtered it out (a controlled <select> with no matching option would
  // silently show the placeholder while still holding the id).
  const licenseOptions = pickedLicense && !licenses.some(l => l.id === pickedLicense.id)
    ? [pickedLicense, ...licenses]
    : licenses;
  const licensesPartial = licenseCounts !== null && licenseCounts.loaded < licenseCounts.total;

  // Radio-group keyboard model: arrows move the selection.
  const onScopeKey = (e: React.KeyboardEvent, index: number) => {
    const last = API_KEY_SCOPES.length - 1;
    let next = -1;
    if (e.key === 'ArrowRight' || e.key === 'ArrowDown') next = index === last ? 0 : index + 1;
    else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') next = index === 0 ? last : index - 1;
    if (next < 0) return;
    e.preventDefault();
    chooseScope(API_KEY_SCOPES[next]!.value);
    (e.currentTarget.parentElement?.children[next] as HTMLElement | undefined)?.focus();
  };

  const activeCount = keys.filter(k => !k.revoked).length;

  return (
    <div>
      <div className="page-header">
        <div>
          <h1 className="page-title">API Keys</h1>
          <p className="page-subtitle">Manage machine-to-machine authentication keys</p>
        </div>
      </div>

      {/* New key created banner */}
      {newKey && (
        <div style={{ padding: '16px', background: '#F0FDF4', border: '1px solid #D1FAE5', borderRadius: '8px', marginBottom: '16px' }}>
          <div style={{ fontSize: '13px', fontWeight: 600, color: '#16A34A', marginBottom: '8px' }}>
            API key created — copy it now, you won't see it again!
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            {/* ps-no-capture: rrweb blockClass. The plaintext API key exists in
                the DOM only here — a <code>, not an input, so maskAllInputs does
                not touch it. Fenced unconditionally, not on showNewKey. */}
            <code className="ps-no-capture" style={{ flex: 1, fontSize: '13px', padding: '8px 12px', background: 'var(--surface)', borderRadius: '6px', border: '1px solid #D1FAE5', fontFamily: 'monospace', wordBreak: 'break-all' }}>
              {showNewKey ? newKey : '•'.repeat(newKey.length)}
            </code>
            <button onClick={() => setShowNewKey(!showNewKey)} style={{ padding: '8px', borderRadius: '6px', border: '1px solid #D1FAE5', background: 'var(--surface)', cursor: 'pointer' }}>
              {showNewKey ? <EyeOff size={14} /> : <Eye size={14} />}
            </button>
            <button onClick={() => copyKey(newKey, 'new')} style={{ padding: '8px 12px', borderRadius: '6px', border: 'none', background: '#16A34A', color: 'white', cursor: 'pointer', fontSize: '13px', display: 'flex', alignItems: 'center', gap: '4px' }}>
              <Copy size={14} /> {copiedId === 'new' ? 'Copied!' : 'Copy'}
            </button>
            <button onClick={() => setNewKey(null)} style={{ padding: '8px', borderRadius: '6px', border: '1px solid #D1FAE5', background: 'var(--surface)', cursor: 'pointer', color: '#16A34A' }}>✕</button>
          </div>
        </div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '16px' }}>
        {/* Key List */}
        <div>
          <div className="card" style={{ padding: 0 }}>
            <div className="card-header" style={{ padding: '14px 16px' }}>
              <span className="card-title">Active API Keys</span>
              <span style={{ fontSize: '12px', color: 'var(--text-muted)' }}>{activeCount} active</span>
            </div>
            {loading ? (
              <div style={{ padding: '40px', textAlign: 'center', color: 'var(--text-muted)', fontSize: '13px' }}>Loading...</div>
            ) : keysError ? (
              <div role="alert" style={{ padding: '32px 40px', textAlign: 'center', fontSize: '13px' }}>
                <div style={{ color: '#DC2626', fontWeight: 600 }}>Could not load API keys</div>
                <div style={{ color: 'var(--text-muted)', marginTop: '4px' }}>{keysError}</div>
                <div style={{ color: 'var(--text-muted)', fontSize: '12px', marginTop: '4px' }}>This is not an empty list: the server did not answer.</div>
                <button
                  type="button"
                  onClick={loadKeys}
                  style={{ marginTop: '12px', padding: '6px 14px', borderRadius: '6px', border: '1px solid var(--border)', background: 'var(--surface)', cursor: 'pointer', fontSize: '12px', fontWeight: 600 }}
                >
                  Retry
                </button>
              </div>
            ) : keys.length === 0 ? (
              <div style={{ padding: '40px', textAlign: 'center', color: 'var(--text-muted)', fontSize: '13px' }}>
                <Key size={20} style={{ marginBottom: '8px', opacity: 0.5 }} />
                <div>No API keys yet</div>
                <div style={{ fontSize: '12px', marginTop: '4px' }}>Create one using the form to get started.</div>
              </div>
            ) : (
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ borderBottom: '1px solid var(--border)' }}>
                      <th scope="col" style={th}>Key</th>
                      <th scope="col" style={th}>Scope</th>
                      <th scope="col" style={th}>Quota</th>
                      <th scope="col" style={th}>Last used</th>
                      <th scope="col" style={th}><span style={visuallyHidden}>Actions</span></th>
                    </tr>
                  </thead>
                  <tbody>
                    {keys.map(key => (
                      <tr key={key.id} style={{ borderBottom: '1px solid var(--border)', opacity: key.revoked ? 0.5 : 1 }}>
                        <td style={td}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                            <Key size={14} style={{ color: key.revoked ? 'var(--text-muted)' : 'var(--accent)' }} />
                            <span style={{ fontSize: '14px', fontWeight: 600 }}>{key.name}</span>
                            {key.revoked && <span style={{ fontSize: '11px', padding: '1px 6px', borderRadius: '4px', background: '#FEE2E2', color: '#DC2626' }}>Revoked</span>}
                          </div>
                          <code style={{ fontSize: '12px', color: 'var(--text-secondary)', fontFamily: 'monospace' }}>
                            {key.key_prefix}••••••••••••
                          </code>
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px 12px', marginTop: '6px', fontSize: '11px', color: 'var(--text-muted)' }}>
                            {key.created_at && <span>Created {formatRelativeTime(key.created_at)}</span>}
                            {key.expires_at && <span>Expires {new Date(key.expires_at).toLocaleDateString()}</span>}
                            {key.license_id && <span title={key.license_id}>Bound to license {key.license_id.slice(0, 8)}…</span>}
                          </div>
                        </td>
                        <td style={td}>
                          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: '6px' }}>
                            {key.scope
                              ? <span style={{ fontSize: '10px', padding: '2px 6px', borderRadius: '4px', background: 'var(--accent-light)', color: 'var(--accent)', fontWeight: 600, textTransform: 'uppercase' }}>{key.scope}</span>
                              : <span style={{ color: 'var(--text-muted)' }}>—</span>}
                            {key.push_status && (
                              <span
                                aria-label={`Push status: ${key.push_status}`}
                                title="State of this key's push to the data plane"
                                style={{ fontSize: '10px', padding: '2px 6px', borderRadius: '9999px', fontWeight: 600, background: PUSH_STYLE[key.push_status].bg, color: PUSH_STYLE[key.push_status].fg }}
                              >
                                {key.push_status}
                              </span>
                            )}
                          </div>
                        </td>
                        <td style={td}>
                          <div
                            style={{ fontSize: '12px', color: 'var(--text-secondary)' }}
                            title={key.max_calls_per_day === undefined ? 'This server version does not report a per-key daily quota' : undefined}
                          >
                            {quotaText(key.max_calls_per_day)}
                          </div>
                          {key.call_count !== undefined && (
                            <div style={{ fontSize: '11px', color: 'var(--text-muted)', marginTop: '4px' }}>{key.call_count.toLocaleString()} calls</div>
                          )}
                        </td>
                        <td style={{ ...td, fontSize: '12px', color: 'var(--text-secondary)' }}>
                          {key.last_used_at ? formatRelativeTime(key.last_used_at) : 'Never'}
                        </td>
                        <td style={{ ...td, textAlign: 'right' }}>
                          {!key.revoked && (
                            <button onClick={() => setRevokeTarget({ id: key.id, name: key.name })} style={{ padding: '4px 8px', borderRadius: '6px', border: '1px solid #FEE2E2', background: '#FFF5F5', color: '#DC2626', cursor: 'pointer', fontSize: '12px', display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                              <Trash2 size={12} /> Revoke
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>

        {/* Create form */}
        <div>
          <div className="card">
            <div className="card-header"><span className="card-title">Create New Key</span></div>
            <div className="card-body">
              <form onSubmit={createKey} style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
                <div>
                  <label htmlFor="api-key-name" style={fieldLabel}>Key Name *</label>
                  <input
                    id="api-key-name"
                    type="text"
                    value={form.name}
                    onChange={e => setForm(prev => ({ ...prev, name: e.target.value }))}
                    placeholder="e.g. Production Server"
                    required
                    style={fieldInput}
                  />
                </div>

                <div>
                  <span id="api-key-scope-label" style={fieldLabel}>Scope</span>
                  <div role="radiogroup" aria-labelledby="api-key-scope-label" aria-describedby="api-key-scope-help" style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                    {API_KEY_SCOPES.map((s, i) => {
                      const on = form.scope === s.value;
                      return (
                        <button
                          key={s.value}
                          type="button"
                          role="radio"
                          aria-checked={on}
                          // Roving tabindex: the group is one tab stop.
                          tabIndex={on || (form.scope === '' && i === 0) ? 0 : -1}
                          onClick={() => chooseScope(s.value)}
                          onKeyDown={e => onScopeKey(e, i)}
                          style={{ padding: '4px 10px', borderRadius: '6px', border: `1px solid ${on ? 'var(--accent)' : 'var(--border)'}`, background: on ? 'var(--accent-light)' : 'var(--surface)', color: on ? 'var(--accent)' : 'var(--text-secondary)', cursor: 'pointer', fontSize: '12px', fontWeight: on ? 600 : 400 }}
                        >
                          {s.value}
                        </button>
                      );
                    })}
                  </div>
                  <div id="api-key-scope-help" data-testid="scope-description" aria-live="polite" style={{ marginTop: '6px', fontSize: '12px', color: 'var(--text-muted)' }}>
                    {selected ? selected.description : 'Choose what this key may do. One key, one scope; mint two keys for two jobs.'}
                  </div>
                </div>

                {needsLicense && (
                  <div>
                    <label htmlFor="api-key-license" style={fieldLabel}>License</label>
                    {licensesSearchable && (
                      <input
                        type="search"
                        aria-label="Search licenses"
                        value={licenseSearch}
                        onChange={e => setLicenseSearch(e.target.value)}
                        placeholder="Search by license ID"
                        style={{ ...fieldInput, marginBottom: '6px' }}
                      />
                    )}
                    <select
                      id="api-key-license"
                      value={form.licenseId}
                      onChange={e => chooseLicense(e.target.value)}
                      disabled={licensesStatus !== 'ready' && licenseOptions.length === 0}
                      style={fieldInput}
                    >
                      <option value="">{licensesStatus === 'loading' ? 'Loading licenses…' : 'Choose a license…'}</option>
                      {licenseOptions.map(l => <option key={l.id} value={l.id}>{l.label}</option>)}
                    </select>
                    {licensesStatus === 'error' && (
                      <div role="alert" style={{ marginTop: '6px', fontSize: '12px', color: '#DC2626' }}>
                        Could not load licenses. A validate key must be bound to one.{' '}
                        <button
                          type="button"
                          onClick={() => setLicenseRetry(n => n + 1)}
                          style={{ padding: 0, border: 'none', background: 'none', color: 'inherit', textDecoration: 'underline', cursor: 'pointer', fontSize: '12px' }}
                        >
                          Retry
                        </button>
                      </div>
                    )}
                    {licensesStatus === 'ready' && licensesPartial && (
                      <div data-testid="license-truncated" style={{ marginTop: '6px', fontSize: '12px', color: 'var(--text-muted)' }}>
                        Showing {licenseCounts!.loaded} of {licenseCounts!.total} install and platform licenses (newest first). Search by license ID to find an older one.
                      </div>
                    )}
                    {licensesStatus === 'ready' && licenseOptions.length === 0 && (
                      <div style={{ marginTop: '6px', fontSize: '12px', color: 'var(--text-muted)' }}>
                        {licenseSearch.trim() ? `No install or platform license matches "${licenseSearch.trim()}".` : 'No install or platform licenses to bind to.'}
                      </div>
                    )}
                  </div>
                )}

                <div>
                  <label htmlFor="api-key-expires" style={fieldLabel}>Expires (optional)</label>
                  <input
                    id="api-key-expires"
                    type="date"
                    value={form.expires}
                    onChange={e => setForm(prev => ({ ...prev, expires: e.target.value }))}
                    style={fieldInput}
                  />
                  {formError && <div role="alert" style={{ marginTop: '6px', fontSize: '12px', color: '#DC2626' }}>{formError}</div>}
                </div>

                <button
                  type="submit"
                  disabled={!canSubmit}
                  style={{ padding: '10px', borderRadius: '8px', background: canSubmit ? 'var(--accent)' : 'var(--accent-muted)', color: 'white', border: 'none', cursor: canSubmit ? 'pointer' : 'not-allowed', fontSize: '13px', fontWeight: 600, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px' }}
                >
                  <Plus size={14} /> {creating ? 'Creating...' : 'Create API Key'}
                </button>
              </form>
            </div>
          </div>

          <div className="card" style={{ marginTop: '12px' }}>
            <div className="card-header"><span className="card-title">Scope Reference</span></div>
            <div className="card-body">
              {API_KEY_SCOPES.map(({ value, description }) => (
                <div key={value} style={{ padding: '6px 0', fontSize: '12px' }}>
                  <code style={{ color: 'var(--accent)', fontWeight: 600 }}>{value}</code>
                  <div style={{ color: 'var(--text-secondary)', marginTop: '2px' }}>{description}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      <ConfirmDialog
        open={revokeTarget !== null}
        title="Revoke API key?"
        body={revokeTarget ? `Revoke API key "${revokeTarget.name}"? This cannot be undone.` : ''}
        confirmLabel="Revoke"
        danger
        onConfirm={() => {
          if (revokeTarget) revokeKey(revokeTarget.id, revokeTarget.name);
          setRevokeTarget(null);
        }}
        onCancel={() => setRevokeTarget(null)}
      />
    </div>
  );
}
